import {
  Card,
  CardContent,
  Skeleton,
  Stack,
  SxProps,
  Theme,
} from "@mui/material";
import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {translate} from "@util";
import {Sensor, Measure} from "@core/model";
import {EmptyData} from "@components/EmptyData";
import {Selectors} from "@core/store/selectors";
import {Multivalue} from "@core/model/Measure";
import {SensorIcon} from "@components/icons";
import {SensorSelector} from "./Selector";
import {MonitoringGrid} from "./Grid";
import {MonitoringHorizontal} from "./Horizontal";
import {MonitoringVertical} from "./Vertical";
import {ChartLoading} from "@components/measure-chart/ChartLoading";

export type MeasureMap = {
  [field: string]: Measure<number | Multivalue>[];
};
export type LoadingMap = {
  [field: string]: boolean;
};

const Monitoring = ({
  display = "grid",
  hideSelector = false,
  limit = 6,
  sx,
}: {
  display?: "grid" | "horizontal" | "vertical";
  hideSelector?: boolean;
  limit?: number;
  sx?: SxProps<Theme>;
}) => {
  const measures = useSelector(Selectors.liveMeasures);

  const sensors = useSelector(Selectors.sensors);
  const [selected, setSelected] = useState<Sensor[]>([]);
  const [measureMap, setMeasureMap] = useState<MeasureMap>({});

  useEffect(() => {
    if (sensors) {
      setSelected(sensors.slice(0, limit));
    }
  }, [sensors]);

  useEffect(() => {
    selected.forEach(s => {
      updateMeasureMap(
        s.name,
        measures.filter(m => m.name === s.name),
      );
    });
  }, [measures, selected]);

  const updateMeasureMap = (
    key: string,
    measures: Measure<number | Multivalue>[],
  ) => {
    setMeasureMap(prev => {
      const newMap = {...prev};
      if (!newMap[key]) {
        newMap[key] = [];
      }

      measures.forEach(m => {
        if (m.id && !newMap[key].find(me => me.id === m.id)) {
          newMap[key].push(m);
        }
      });

      const start = Math.max(newMap[key].length - 10, 0);
      const end = newMap[key].length;

      newMap[key] = newMap[key]
        .sort((a, b) => a.timestamp - b.timestamp)
        .slice(start, end);

      return newMap;
    });
  };

  const handleChange = (s: Sensor, checked: boolean) => {
    setSelected(old => {
      if (checked) {
        if (old.length >= limit) return old;
        return [...old, s];
      } else {
        return [...old].filter(c => s.name !== c.name);
      }
    });
  };

  if (!sensors && display === "vertical") {
    return (
      <Stack direction={"column"} gap={2} flex={1}>
        {Array.from({length: 3}).map((_, index) => (
          <Card
            key={`skeleton-monitoring-${index}`}
            variant="outlined"
            sx={{flex: 1, display: "flex", flexDirection: "column"}}>
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}>
              <Skeleton variant="text" />
              <ChartLoading sx={{margin: "auto 0"}} />
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  if (!sensors) {
    return (
      <Card
        sx={{
          flex: "1 1 0",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
        }}>
        <Stack direction={"row"} gap={2} flex={1}>
          {Array.from({length: 3}).map((_, index) => (
            <Card
              key={`skeleton-monitoring-${index}`}
              variant="outlined"
              sx={{flex: 1, display: "flex", flexDirection: "column"}}>
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}>
                <Skeleton variant="text" />
                <ChartLoading sx={{margin: "auto 0"}} />
              </CardContent>
            </Card>
          ))}
        </Stack>
        {display === "grid" && (
          <Stack direction={"row"} gap={2} flex={1}>
            {Array.from({length: 3}).map((_, index) => (
              <Card
                key={`skeleton-monitoring-${index}`}
                variant="outlined"
                sx={{flex: 1, display: "flex", flexDirection: "column"}}>
                <CardContent
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}>
                  <Skeleton variant="text" />
                  <ChartLoading sx={{margin: "auto 0"}} />
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Card>
    );
  }

  if (sensors.length === 0) {
    return (
      <Card sx={{flex: "1 1 0", display: "flex", ...sx}}>
        <EmptyData
          Icon={SensorIcon}
          message={translate("common.label.empty.sensors")}
        />
      </Card>
    );
  }
  return (
    <>
      {!hideSelector && sensors.length > 1 && (
        <SensorSelector
          collapse={display === "horizontal"}
          sensors={sensors}
          selected={selected}
          handleChange={handleChange}
        />
      )}

      {display === "grid" && (
        <MonitoringGrid
          limit={limit}
          selected={selected}
          measureMap={measureMap}
          sx={{...sx}}
        />
      )}

      {display === "horizontal" && (
        <MonitoringHorizontal
          limit={limit}
          selected={selected}
          measureMap={measureMap}
          sx={{...sx}}
        />
      )}

      {display === "vertical" && (
        <MonitoringVertical
          limit={limit}
          selected={selected}
          measureMap={measureMap}
          sx={{...sx}}
        />
      )}
    </>
  );
};

export {Monitoring};
